/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-10
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ButtonComponent, IconComponent, InputComponent } from 'nebula-ui-kit';
import { TranslatePipe } from '@nebula-shared/data-access';
import { CommonModule } from '@angular/common';
import { CompactTable } from '@nebula-shared/domain';

@Component({
  selector: 'app-table-compact',
  imports: [CommonModule, IconComponent, InputComponent, TranslatePipe, ButtonComponent],
  templateUrl: './table-compact.html',
  styleUrl: './table-compact.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Tabla compacta reutilizable para editar o consultar detalles dentro de un formulario.
 *
 * La configuración declarativa {@link CompactTable} define columnas, resumen y acciones.
 * El componente no modifica el arreglo recibido: emite los cambios de cada celda mediante
 * {@link changeData}, para que el contenedor mantenga la fuente de datos.
 *
 * @typeParam T Tipo de fila que muestra la tabla.
 *
 * @remarks
 * - {@link action} comunica acciones de cabecera o fila junto con su índice opcional.
 * - {@link searchInput} comunica la solicitud de búsqueda de una celda de tipo búsqueda.
 * - {@link changeData} entrega una copia de la fila con el valor de la columna actualizado.
 */
export class TableCompact<T> {
  config = input.required<CompactTable>();
  hasAction = computed(() => this.config()?.actions ?? false);
  actionsHeader = computed(() => {
    if (this.hasAction()) {
      return this.config()?.actions?.filter((action) => action.encabezado);
    }
    return [];
  });
  actionsBody = computed(() => {
    if (this.hasAction()) {
      return this.config()?.actions?.filter((action) => !action.encabezado);
    }
    return [];
  });
  hasCounter = computed(() => this.config()?.counter ?? false);
  hasSummary = computed(() => this.config()?.summary ?? false);
  action = output<{ action: string; index?: number }>();
  searchInput = output<{
    row: T;
    column: string;
    index: number;
  }>();
  changeData = output<{
    row: T;
    column: string;
    index: number;
  }>();
  data = input<T[]>([]);

  sendAction(action: string, index?: number): void {
    this.action.emit({ action, index });
  }

  sendSearchInput(row: T, column: string, index: number): void {
    this.searchInput.emit({
      row,
      column,
      index,
    });
  }

  sendChangeData(row: T, column: string, index: number, data: any): void {
    const updateData = {
      ...row,
      [column]: data,
    };
    this.changeData.emit({
      row: updateData,
      column,
      index,
    });
  }

  getValue(row: T, key: string): any {
    return (row as Record<string, unknown>)[key] ?? '';
  }
}
